// AdAccountList.tsx - Material React Table 버전
import React, { SetStateAction, useEffect, useState } from 'react';
import { MaterialReactTable } from 'material-react-table';
import { Box, IconButton } from '@mui/material';
import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import { PromoListData } from './useLoadPromoList';

const TableHeaderSx = {
  backgroundColor: '#30515B',
  color: 'white',
  fontSize: '14px',
  textAlign: 'center',
  '& .Mui-TableHeadCell-Content': {
    justifyContent: 'center',
    alignItems: 'center',
    display: 'flex',
    height: '100%',
  },
  '&:first-of-type': {
    borderTopLeftRadius: '5px',
    borderBottomLeftRadius: '5px',
  },
  '&:last-of-type': {
    borderTopRightRadius: '5px',
    borderBottomRightRadius: '5px',
  },
}

const TableRowSx = {
  height: '49px',
  '& > td:first-of-type': {
    borderTopLeftRadius: '5px',
    borderBottomLeftRadius: '5px',
  },
  '& > td:last-of-type': {
    borderTopRightRadius: '5px',
    borderBottomRightRadius: '5px',
  },
  '& > td': {
    background: '#FFF',
    boxShadow: '1px 2px 2px rgba(0, 0, 0, 0.1)',
    padding: '4px',
  },
}

const TableCellSx = {
  textAlign: 'center',
  verticalAlign: 'middle',
  fontSize: '16px',
}

export const getStatusFromRow = (row: any): string => {
  const { is_active, admin_approval } = row;
  if (!is_active && !admin_approval) return '대기';
  if (!is_active && admin_approval) return '차단';
  if (is_active && admin_approval) return '승인';
  return ''; // 기타 상태는 필터링 제외
};

interface Props{
  propsData : PromoListData[]
  totalPage: number
  setPage : React.Dispatch<SetStateAction<number>>
  page : number
  startdate : string|null
  enddate: string|null
  setSortBy : React.Dispatch<SetStateAction<string>>
  setSortOrder : React.Dispatch<SetStateAction<string>>
  sortBy:string,
  sortOrder:string
}

const Promo_ListTable = ({propsData, totalPage, setPage, page, startdate,enddate, setSortBy, setSortOrder,sortBy,sortOrder}:Props) => {
  const [pagination, setPagination] = useState({
    pageIndex: page-1,
    pageSize: 8,
  });
  useEffect(() => {
    setPagination((prev) => ({
      ...prev,
      pageIndex: page - 1, // 상태는 0-based
    }));
  }, [page]);
  useEffect(()=>{
    setPagination((prev) => ({ ...prev, pageIndex: 0 }))
  },[startdate,enddate])
  const totalPages = totalPage;
  const handlePageChange = (page: number) => {
    setPagination((prev) => ({ ...prev, pageIndex: page }));
    setPage(page+1); // 부모에게 알림
  };

  return (
    <div style={{height:'100%'}}>
    <MaterialReactTable
      data={propsData}
      muiTableContainerProps={{
        sx: { 
          height: 'calc(100vh - 450px)',
          overflowY: 'auto',
          overflowX: 'auto',
          width: 'calc(100vw - 425px)'
        }
      }}
      columns={[
        {
          header: '순서',
          Cell: ({ row }) => pagination.pageIndex * pagination.pageSize + row.index + 1,
          size: 50, // 필요에 따라 너비 지정
          enableSorting: false, // ✅ 정렬 비활성화
        },
        { accessorKey:'school_name',header: '학교명'},
        {
          id:'wide_loc',
          header:'지역',
          accessorFn:(row)=>`${row.wide_loc} ${row.city_loc}`,
        },
        { accessorKey: 'visit_date', header: '방문일자' },
        { accessorKey: 'total_cnt', header: '전체 학생 수' },
        { accessorKey: 'attendee_cnt', header: '참여자 수' },
        {
          id:'interested_cnt',
          header: '관심표명자 수(%)',
          accessorFn: (row) => ({
            count: row.interested_cnt,
            percent:
              row.attendee_cnt && row.attendee_cnt !== 0
                ? (row.interested_cnt / row.attendee_cnt) * 100
                : 0,
          }),
          Cell: ({ cell }) => {
            const { count, percent } = cell.getValue() as { count: number; percent: number };
            return `${count} (${percent.toFixed(1)}%)`;
          },
        },
        {
          id:'apply_cnt',
          header: '지원자 수(%)',
          accessorFn: (row) => ({
            count: row.apply_cnt,
            percent:
              row.interested_cnt && row.interested_cnt !== 0
                ? (row.apply_cnt / row.interested_cnt) * 100
                : 0,
          }),
          Cell: ({ cell }) => {
            const { count, percent } = cell.getValue() as { count: number; percent: number };
            return `${count} (${percent.toFixed(1)}%)`;
          },
        },
        {
          id:'commissioned_count',
          header: '임관 수(%)',
          accessorFn: (row) => ({
            count: row.commissioned_count,
            percent:
              row.apply_cnt && row.apply_cnt !== 0
                ? (row.commissioned_count / row.apply_cnt) * 100
                : 0,
          }),
          Cell: ({ cell }) => {
            const { count, percent } = cell.getValue() as { count: number; percent: number };
            return `${count} (${percent.toFixed(1)}%)`;
          },
        },
        {
          id:'not_interested_count',
          header: '관심상실자 수(%)',
          accessorFn: (row) => ({
            count: row.not_interested_count,
            percent:
              row.interested_cnt && row.interested_cnt !== 0
                ? (row.not_interested_count / row.interested_cnt) * 100
                : 0,
          }),
          Cell: ({ cell }) => {
            const { count, percent } = cell.getValue() as { count: number; percent: number };
            return `${count} (${percent.toFixed(1)}%)`;
          },
        },
      ]}
      // 툴바, sort 버튼 등
      enableTopToolbar={false}
      enableBottomToolbar={false}
      enableColumnActions={false}
      enableSorting={true}
      muiTablePaperProps={{
        elevation: 0, // ✅ 그림자 제거
      }}
      // 로우 헤더 스타일
      muiTableHeadCellProps={{
        sx: TableHeaderSx,
      }}
      // 로우 스타일
      muiTableBodyRowProps={{
      sx: TableRowSx,
      }}
      // 셀 스타일
      muiTableBodyCellProps={{
        sx: TableCellSx,
      }}
      // 테이블 스타일
      muiTableProps={{
        sx: {
          borderCollapse: 'separate',
          borderSpacing: '0 5px', // ✅ row 간격 5px
          '& .MuiTableSortLabel-icon': {
            color: '#aaa',
            fontSize: '1.5rem',
          },
          '& .MuiTableSortLabel-root.Mui-active .MuiTableSortLabel-icon': {
            color: 'rgb(255, 255, 255) !important',
          },
      }}}
      // 페이지 네이션 옵션
      state={{ pagination,
        sorting: sortBy
        ? [
            {
              id: sortBy,
              desc: sortOrder === 'desc',
            },
          ]
        : [],
       }}
      manualPagination 
      pageCount={totalPages} // 수동 계산 필요 시
      // 솔팅 옵션
      manualSorting
      enableSortingRemoval={false}
      onSortingChange={(updater)=>{
        const sorting = typeof updater === 'function' ? updater([]) : updater;

        if (sorting.length > 0) {
          const sort = sorting[0];
          setSortBy(sort.id); // 정렬할 컬럼의 accessorKey 또는 accessorFn id
          // setTimeout(() => {
            setSortOrder(sort.desc ? 'desc' : 'asc');
          // }, 300);

        } else {
          // 정렬 초기화 시
          setSortBy('');
          setSortOrder('');
        }
      }}
      />
        <Box display="flex" justifyContent="center" mt={2} gap={1}>
        {/* 이전 버튼 */}
        <IconButton
          onClick={() => handlePageChange(Math.max(pagination.pageIndex - 1, 0))}
          disabled={pagination.pageIndex === 0}
          aria-label="이전 페이지"
        >
          <ChevronLeft />
        </IconButton>

        {/* 숫자 버튼 */}
        {Array.from({ length: totalPage }).map((_, index) => (
          <IconButton
            key={index}
            onClick={() => handlePageChange(index)}
            sx={{
              backgroundColor:
                pagination.pageIndex === index ? '#50818b' : 'transparent',
              color: pagination.pageIndex === index ? '#fff' : '#666',
              '&:hover': {
                backgroundColor:
                  pagination.pageIndex === index ? '#4a747d' : '#f0f0f0',
              },
              width: 32,
              height: 32,
              fontSize: 14,
            }}
          >
            {index + 1}
          </IconButton>
        ))}

        {/* 다음 버튼 */}
        <IconButton
          onClick={() =>
            handlePageChange(Math.min(pagination.pageIndex + 1, totalPage - 1))
          }
          disabled={pagination.pageIndex === totalPage - 1}
          aria-label="다음 페이지"
        >
          <ChevronRight />
        </IconButton>
      </Box>
    </div>
  );
};

export default Promo_ListTable;
